import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useToast } from '@/hooks/errors/use-toast';
import { useSoftDeleteWorkbookImage } from '@/hooks/content-generator/use-workbook-images';
import type { PrivateImageLibraryItem } from '../types';

/**
 * Hook for soft deleting private images
 * Uses soft delete to preserve editor references while hiding from private library
 */
export function useDeletePrivateImage() {
  // Return the soft delete hook directly since it has all the logic we need
  return useSoftDeleteWorkbookImage();
}

/**
 * Hook for downloading private images
 */
export function useDownloadPrivateImage() {
  const { toast } = useToast();

  return useCallback(async (image: PrivateImageLibraryItem) => {
    try {
      // Fetch the image blob
      const response = await fetch(image.image_url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = image.filename || `image-${image.image_id}.jpg`;
      
      // Trigger download
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Download started",
        description: `Downloading ${image.filename}`,
      });
    } catch (error) {
      console.error('Download image error:', error);
      toast({
        title: "Download failed",
        description: "Failed to download image. Please try again.",
        variant: "destructive",
      });
    }
  }, [toast]);
}

/**
 * Hook to format file size for display
 */
export function useFormatFileSize() {
  return useCallback((bytes: number | null): string => {
    if (!bytes) return 'Unknown size';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = (bytes / Math.pow(1024, i)).toFixed(1);
    
    return `${size} ${sizes[i]}`;
  }, []);
}

/**
 * Hook to format image dimensions for display
 */
export function useFormatDimensions() {
  return useCallback((width: number | null, height: number | null): string => {
    if (!width || !height) return 'Unknown dimensions';
    return `${width} × ${height}`;
  }, []);
} 